import PropTypes from "prop-types";
import { FileIcon, RotateCw, XIcon } from "lucide-react";

import SavedAttachment, { describeFile } from "components/SavedAttachment";

import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentGroup,
  AttachmentMedia,
  AttachmentTitle,
} from "@/components/ui/attachment";
import { Spinner } from "@/components/ui/spinner";

const STATE_BY_STATUS = {
  queued: "idle",
  uploading: "uploading",
  finished: "done",
  failed: "error",
};

const describeUpload = upload => {
  switch (upload.status) {
    case "queued":
      return "Waiting";
    case "uploading":
      return `Uploading · ${upload.percent}%`;
    case "failed":
      return upload.error || "Upload failed";
    default:
      return describeFile(upload.file.type, upload.file.size);
  }
};

function UploadRow({ upload, onRemove, onRetry }) {
  const { file, status } = upload;
  return (
    <Attachment state={STATE_BY_STATUS[status]} size="sm">
      <AttachmentMedia>
        {status === "uploading" ? <Spinner /> : <FileIcon />}
      </AttachmentMedia>
      <AttachmentContent>
        <AttachmentTitle>{file.name}</AttachmentTitle>
        <AttachmentDescription>{describeUpload(upload)}</AttachmentDescription>
      </AttachmentContent>
      <AttachmentActions>
        {status === "failed" && (
          <AttachmentAction
            aria-label={`Retry ${file.name}`}
            onClick={() => onRetry(upload)}
          >
            <RotateCw />
          </AttachmentAction>
        )}
        <AttachmentAction
          aria-label={`Remove ${file.name}`}
          onClick={() => onRemove(upload)}
        >
          <XIcon />
        </AttachmentAction>
      </AttachmentActions>
    </Attachment>
  );
}

function UploadAttachmentList({ uploads, existing, onRemove, onRetry }) {
  // A note snapshot carries no attachments, and flask-restx marshals a
  // missing list as null rather than omitting it, so a parameter default
  // would not cover it.
  const queued = uploads ?? [];
  const saved = existing ?? [];

  if (queued.length === 0 && saved.length === 0) return null;

  return (
    <AttachmentGroup className="tw:mt-3 tw:flex-wrap">
      {saved.map(attachment => (
        <SavedAttachment
          key={attachment.id ?? attachment.url}
          attachment={attachment}
        />
      ))}
      {queued.map(upload => (
        <UploadRow
          key={upload.id}
          upload={upload}
          onRemove={onRemove}
          onRetry={onRetry}
        />
      ))}
    </AttachmentGroup>
  );
}

UploadAttachmentList.propTypes = {
  uploads: PropTypes.arrayOf(PropTypes.object),
  existing: PropTypes.arrayOf(PropTypes.object),
  onRemove: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export default UploadAttachmentList;
